import React from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Shield, CheckCircle2, Camera, TrendingUp,
  Thermometer, Volume2, Eye, Gauge, Beaker, Droplets, Wind, Video, Waves
} from 'lucide-react';
import useSEO from '../hooks/useSEO';
import leakPhoto from '../assets/dde.webp';

const detectionMethods = [
  {
    icon: Thermometer,
    to: '/techniques/camera-thermique',
    title: 'Inspection thermographique',
    description: 'Caméra thermographique pour analyser les variations de température et visualiser les écoulements avec précision exceptionnelle.'
  },
  {
    icon: Volume2,
    to: '/techniques/detection-acoustique',
    title: 'Écoute acoustique',
    description: "Corrélation acoustique pour détecter les fuites en distinguant les sons émis par l'eau qui jaillit des conduites."
  },
  {
    icon: Eye,
    to: '/techniques/endoscopie',
    title: 'Endoscopie caméra',
    description: 'Caméras endoscopiques pour explorer les zones inaccessibles et identifier les défauts dans vos installations.'
  },
  {
    icon: Gauge,
    to: '/techniques/mise-en-pression',
    title: 'Contrôle de pression',
    description: "Test de pression simple et efficace pour révéler rapidement les défauts d'étanchéité de vos conduites."
  },
  {
    icon: Beaker,
    to: '/techniques/gaz-traceur',
    title: 'Test au gaz traceur',
    description: 'Injection de gaz traceur pour détecter même les plus petites fuites dans vos canalisations.'
  },
  {
    icon: Droplets,
    to: '/techniques/fluoresceine',
    title: 'Test à la fluorescéine',
    description: "Moyen efficace pour détecter les fuites dans les conduites d'évacuation avec une précision remarquable."
  },
  {
    icon: Waves,
    to: '/techniques/humidimetre',
    title: "Mesure d'humidité",
    description: "Cartographie de l'humidité des matériaux pour délimiter la zone touchée et savoir de quel côté vient l'eau."
  },
  {
    icon: Wind,
    to: '/techniques/test-fumigene',
    title: 'Test fumigène',
    description: "Fumée non toxique envoyée dans les évacuations pour révéler les raccords non étanches et l'origine des odeurs."
  },
  {
    icon: Video,
    to: '/techniques/camera-canalisation',
    title: "Caméra d'inspection",
    description: "Caméra poussée dans les canalisations d'évacuation et les égouts pour voir fissures, joints déboîtés et racines."
  }
];

const expertiseServices = [
  {
    title: 'Assurance incendie - heurt',
    description: "Analyse des dégâts causés par un incendie ou un heurt pour évaluer précisément les dommages."
  },
  {
    title: 'Assurance vol',
    description: "Évaluation des pertes et dégâts matériels après un cambriolage ou une tentative d'effraction."
  },
  {
    title: 'Assurance tempête',
    description: "Expertise des dommages causés par les intempéries : vents violents, grêle ou inondations."
  },
  {
    title: 'Dégâts des eaux',
    to: '/degat-des-eaux',
    description: "Identification des causes et estimation des réparations pour limiter les impacts financiers."
  },
  {
    title: 'Responsabilité civile',
    description: 'Analyse des réclamations pour déterminer la responsabilité et les conséquences financières.'
  }
];

const approachSteps = [
  { icon: Search, title: 'Analyse', description: 'Diagnostic précis de la situation' },
  { icon: Camera, title: 'Documentation', description: 'Photos et mesures détaillées' },
  { icon: TrendingUp, title: 'Évaluation', description: 'Estimation des dommages' },
  { icon: CheckCircle2, title: 'Rapport', description: 'Compte-rendu complet sous 72h' }
];

const Services = () => {
  useSEO({
    title: "Nos services : recherche de fuite et expertise | Expomis",
    description: "Thermographie, écoute acoustique, gaz traceur, fluorescéine : nos méthodes de recherche de fuite et nos expertises d'assurance (incendie, vol, dégâts des eaux).",
    path: '/services'
  });

  return (
    <div className="pt-40 pb-24">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-20">
          <div className="text-xs font-semibold tracking-widest uppercase text-brand mb-4">
            Nos services
          </div>
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-ink mb-6">
            Des solutions complètes et professionnelles
          </h1>
          <p className="text-lg text-ink-soft leading-relaxed">
            {"Recherche de fuite et expertise d'assurance au Luxembourg, et recherche de fuite dans le "}
            <Link to="/recherche-fuite-province-luxembourg" className="text-brand underline underline-offset-2">sud de la province de Luxembourg</Link>.
          </p>
        </div>

        {/* Détection de fuites */}
        <section className="mb-28">
          <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12 items-center mb-16">
            <img
              src={leakPhoto}
              alt="Détection de fuite d'eau sur une canalisation en cuivre"
              className="w-full h-72 object-cover rounded-sm"
            />
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Search className="w-5 h-5 text-brand" strokeWidth={1.6} />
                <span className="text-xs font-semibold tracking-widest uppercase text-brand">Détection</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-ink mb-4">
                Détection de fuite d'eau
              </h2>
              <p className="text-lg text-ink-soft leading-relaxed">
                Votre solution rapide, efficace et économique pour localiser précisément vos fuites.
              </p>
              <Link to="/recherche-fuite-luxembourg" className="inline-block mt-4 text-sm font-semibold text-brand hover:text-brand-deep">
                Tout savoir sur la recherche de fuite →
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10 mb-16">
            {detectionMethods.map((method, index) => (
              <div key={index} className="border-t-2 border-brand pt-5">
                <method.icon className="w-6 h-6 text-brand mb-4" strokeWidth={1.4} />
                <h3 className="font-serif text-xl font-semibold text-ink mb-2">
                  <Link to={method.to} className="hover:text-brand">{method.title}</Link>
                </h3>
                <p className="text-ink-soft leading-relaxed text-sm">{method.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-white border border-line p-10">
            <h3 className="font-serif text-2xl font-semibold text-ink mb-8 text-center">
              Pourquoi choisir nos services ?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Réactivité à toute épreuve avec prise de rendez-vous rapide',
                'Inspection minutieuse garantie de résultats',
                'Méthodes non destructives préservant vos installations',
                'Remboursement facilité par votre assurance',
                'Rapport complet et détaillé sous 72h',
                'Technologies de pointe pour une précision exceptionnelle'
              ].map((advantage, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand mt-0.5 flex-shrink-0" strokeWidth={1.6} />
                  <span className="text-ink-soft">{advantage}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Expertise assurance */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-brand" strokeWidth={1.6} />
            <span className="text-xs font-semibold tracking-widest uppercase text-brand">Expertise</span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-ink mb-4">
            Expertises professionnelles
          </h2>
          <p className="text-lg text-ink-soft leading-relaxed max-w-2xl mb-16">
            Nous intervenons comme experts mandatés par les compagnies d'assurance, et pouvons également
            agir comme contre-expert indépendant au service des assurés — votre tranquillité assurée
            avec des services d'expertise complets et fiables. En savoir plus sur la <Link to="/contre-expertise" className="text-brand underline underline-offset-2">contre-expertise</Link>.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10 mb-16">
            {expertiseServices.map((service, index) => (
              <div key={index} className="border-t-2 border-brand pt-5">
                <h3 className="font-serif text-xl font-semibold text-ink mb-2">
                  {service.to ? <Link to={service.to} className="hover:text-brand">{service.title}</Link> : service.title}
                </h3>
                <p className="text-ink-soft leading-relaxed text-sm">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-white border border-line p-10">
            <h3 className="font-serif text-2xl font-semibold text-ink mb-10 text-center">Notre approche</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {approachSteps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center border border-brand/30">
                    <step.icon className="w-6 h-6 text-brand" strokeWidth={1.4} />
                  </div>
                  <h4 className="font-semibold text-ink mb-1">{step.title}</h4>
                  <p className="text-sm text-ink-soft">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Services;
